// Prompt.jsx
// Rules:
// - In Library: user cannot Like or Copy their own prompt (prompt.userId === currentUserId).
// - In Collection: once published (isPublic === true), the Publish button becomes "Published" (disabled).
//
// Assumptions:
// - PromptLibrary has: userId (ObjectId/string) and likedBy[].
// - PromptUser has: isPublic (set true by /publish).
// - currentUserId comes from props.userId or localStorage.userId.

import React from "react";
import axios from "axios";
import {
  HandThumbUpIcon as HandThumbUpOutline,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";
import { HandThumbUpIcon as HandThumbUpSolid } from "@heroicons/react/24/solid";
import { WithContext as ReactTags } from "react-tag-input";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";



const API_BASE = process.env.REACT_APP_API_ENDPOINT || "";

const API = {
  collectionList: `${API_BASE}/promptUser/list`,
  collectionAdd: `${API_BASE}/promptUser`,
  collectionUpdate: (id) => `${API_BASE}/promptUser/${id}`,
  collectionDelete: (id) => `${API_BASE}/promptUser/${id}`,
  collectionPublish: (id) => `${API_BASE}/promptUser/${id}/publish`,
  libraryList: `${API_BASE}/promptLibrary/list`,
  libraryCopyToCollection: (id) => `${API_BASE}/promptLibrary/${id}/copy`,
  libraryToggleLike: (id) => `${API_BASE}/promptLibrary/${id}/like`,
   libraryDelete: (id) => `${API_BASE}/promptLibrary/${id}`
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString("en-AU", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};

const smartCap = (word) => {
  const upper = word.toUpperCase();
  const keepCaps = ["PTSD", "ADHD", "GP", "MHCP", "NDIS"];
  if (keepCaps.includes(upper)) return upper;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};


// put near the top (outside the class)
const CATEGORIES = [
  "Consultation",
  "Letters",
  "Reports",
  "Care Plans",
  "Mental Health",
  "Chronic Disease",
  "Preventive Care",
  "Instruction", 
  "Referrals",
  "Admin",
  "Patient Education",
  "Other",
];


export default class Prompt extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: "collection",
      loading: false,
      error: "",
      collection: [],
      library: [],
      selectedId: "",
      form: {
        title: "",
        description: "",
        promptText: "",
        category: "",
        tagsText: "",
      },
      saving: false,
      deleting: false,
      publishing: false,
      copying: false,
      liking: false,
      liked: false,
      tags: [],
      isEditing: false,
    originalForm: null,
    originalTags: [],

    };
  }

  componentDidMount() {
    this.loadActive();
  }

  currentUserId = () =>
    String(this.props.userId || localStorage.getItem("userId") || "");

  setTab = (tab) => {
    this.setState(
      {
        activeTab: tab,
        selectedId: "",
        error: "",
        liked: false,
        // ✅ reset right panel so collection shows placeholders on load
        form: { title: "", description: "", promptText: "", category: "" },
        tags: [],
        originalForm: null,
        originalTags: [],
        isEditing: false,
      },
      this.loadActive
    );
  };


  authHeaders = () => {
    const token = this.props.token || localStorage.getItem("token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  axiosConfig = () => ({
    withCredentials: true,
    headers: { ...this.authHeaders() },
  });

  request = async (method, url, body) => {
    try {
      const config = this.axiosConfig();
      const res =
        method === "get" || method === "delete"
          ? await axios[method](url, config)
          : await axios[method](url, body, config);
      return res.data || {};
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Request failed";
      throw new Error(msg);
    }
  };

  loadActive = async () => {
    const { activeTab } = this.state;
    this.setState({ loading: true, error: "" });

    try {
      if (activeTab === "collection") {
        const data = await this.request("get", API.collectionList);
        const list = data.prompts || data.items || data.collection || [];
        this.setState({ collection: list });
      } else {
        const data = await this.request("get", API.libraryList);
        const list = data.prompts || data.items || data.library || [];
        this.setState({ library: list });
      }
    } catch (e) {
      this.setState({ error: e.message || "Failed to load." });
    } finally {
      this.setState({ loading: false });
    }
  };

  listForActive = () =>
    this.state.activeTab === "collection"
      ? this.state.collection
      : this.state.library;

  getSelected = () => {
    const list = this.listForActive();
    return list.find((x) => x._id === this.state.selectedId) || null;
  };

    isOwnLibraryPrompt = (item) => {
      const uid = String(this.currentUserId() || "");
      if (!uid || !item) return false;

      const raw = item.userId;

      // userId might be:
      // - "65f..." (string)
      // - ObjectId (object)
      // - populated object { _id: "65f...", ... }
      const itemUid =
        raw && typeof raw === "object"
          ? String(raw._id || raw.id || raw.toString?.() || "")
          : String(raw || "");

      return itemUid === uid;
    };



    selectItem = (item) => {
        const uid = this.currentUserId();
        const chips = Array.isArray(item?.tags) ? item.tags.map((t) => ({ id: t, text: t })) : [];

        const liked =
            this.state.activeTab === "library" &&
            Array.isArray(item?.likedBy) &&
            uid &&
            item.likedBy.some((u) => String(u) === uid);

        const nextForm = {
            title: item?.title || "",
            description: item?.description || "",
            promptText: item?.promptText || "",
            category: item?.category || "",
        };

        this.setState({
            selectedId: item?._id || "",
            liked: Boolean(liked),
            tags: chips,
            form: nextForm,
            originalForm: nextForm,
            originalTags: chips,
            isEditing: false, // ✅ lock on view
            error: "",
        });
    };


    newItem = () => {
        this.setState({
            selectedId: "",
            liked: false,
            tags: [],
            form: { title: "", description: "", promptText: "", category: "" },
            originalForm: null,
            originalTags: [],
            isEditing: true, // ✅ new prompt is editable
            error: "",
        });
    };

    startEdit = () => {
        this.setState({ isEditing: true });
    };

    cancelEdit = () => {
        this.setState((prev) => ({
            isEditing: false,
            form: prev.originalForm ? { ...prev.originalForm } : prev.form,
            tags: prev.originalTags ? [...prev.originalTags] : prev.tags,
            error: "",
        }));
    };

    confirmDelete = () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        confirmAlert({
            title: "Confirm delete",
            message: "Are you sure you want to delete this prompt?",
            buttons: [
            { label: "Yes, delete", onClick: () => this.deleteConfirmed() },
            { label: "Cancel" },
            ],
        });
    };

    deleteConfirmed = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ deleting: true, error: "" });
        try {
            await this.request("delete", API.collectionDelete(selectedId));
            this.setState((prev) => ({
            collection: prev.collection.filter((p) => p._id !== selectedId),
            selectedId: "",
            tags: [],
            form: { title: "", description: "", promptText: "", category: "" },
            originalForm: null,
            originalTags: [],
            isEditing: false,
            }));
        } catch (e) {
            this.setState({ error: e.message || "Delete failed." });
        } finally {
            this.setState({ deleting: false });
        }
    };


    handleChange = (field, val) => {
        this.setState((prev) => ({ form: { ...prev.form, [field]: val } }));
    };

    save = async () => {
        const { selectedId, form } = this.state;

        if (!form.title.trim() || !form.promptText.trim()) {
        this.setState({ error: "Title and prompt text are required." });
        return;
        }

        this.setState({ saving: true, error: "" });

        try {
        const payload = {
            title: form.title.trim(),
            description: form.description || "",
            promptText: form.promptText,
            category: form.category || "",
            tags: this.state.tags.map((t) => t.text),
        };

        if (!selectedId) {
            const data = await this.request("post", API.collectionAdd, payload);
            const created = data.prompt || data.userPrompt || data.item;

            this.setState((prev) => ({
            collection: created ? [created, ...prev.collection] : prev.collection,
            selectedId: created?._id || prev.selectedId,
            }));

            if (created) this.selectItem(created);
        } else {
            const data = await this.request(
            "put",
            API.collectionUpdate(selectedId),
            payload
            );
            const updated = data.prompt || data.userPrompt || data.item;

            this.setState((prev) => ({
            collection: prev.collection.map((p) =>
                p._id === selectedId ? updated || p : p
            ),
            }));

            if (updated) this.selectItem(updated);
        }
        } catch (e) {
        this.setState({ error: e.message || "Save failed." });
        } finally {
        this.setState({ saving: false });
        }
    };

    delete = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ deleting: true, error: "" });

        try {
        await this.request("delete", API.collectionDelete(selectedId));
        this.setState((prev) => ({
            collection: prev.collection.filter((p) => p._id !== selectedId),
            selectedId: "",
            form: {
            title: "",
            description: "",
            promptText: "",
            category: "",
            tagsText: "",
            },
        }));
        } catch (e) {
        this.setState({ error: e.message || "Delete failed." });
        } finally {
        this.setState({ deleting: false });
        }
    };

    publishSelected = async () => {
        const { selectedId } = this.state;
        if (!selectedId) return;

        this.setState({ publishing: true, error: "" });

        try {
        await this.request("post", API.collectionPublish(selectedId), {});
        this.setState((prev) => ({
            collection: prev.collection.map((p) =>
            p._id === selectedId ? { ...p, isPublic: true } : p
            ),
        }));
        } catch (e) {
        this.setState({ error: e.message || "Publish failed." });
        } finally {
        this.setState({ publishing: false });
        }
    };

  copySelectedToCollection = async () => {
    const sel = this.getSelected();
    if (!sel) return;
    if (this.isOwnLibraryPrompt(sel)) return;

    this.setState({ copying: true, error: "" });

    try {
      const data = await this.request(
        "post",
        API.libraryCopyToCollection(sel._id),
        {}
      );
      const userPrompt = data.userPrompt;
      if (userPrompt) {
        this.setState((prev) => ({
          collection: [userPrompt, ...prev.collection],
        }));
      }
    } catch (e) {
      this.setState({ error: e.message || "Copy failed." });
    } finally {
      this.setState({ copying: false });
    }
  };

  toggleLike = async () => {
    const sel = this.getSelected();
    if (!sel) return;
    if (this.isOwnLibraryPrompt(sel)) return;

    this.setState({ liking: true, error: "" });

    try {
      const data = await this.request("post", API.libraryToggleLike(sel._id), {});
      const updated = data.prompt;
      const likedNow =
        typeof data.liked === "boolean" ? data.liked : !this.state.liked;

      this.setState((prev) => ({
        liked: likedNow,
        library: updated
          ? prev.library.map((p) => (p._id === sel._id ? updated : p))
          : prev.library,
      }));
    } catch (e) {
      this.setState({ error: e.message || "Like failed." });
    } finally {
      this.setState({ liking: false });
    }
  };

    handleTagDelete = (i) => {
        this.setState((prev) => ({ tags: prev.tags.filter((_, idx) => idx !== i) }));
    };

    handleTagAdd = (tag) => {
        let text = String(tag?.text || "").trim();
        if (!text) return;

        text = smartCap(text);

        this.setState((prev) => {
            const exists = prev.tags.some(
            (t) => t.text.toLowerCase() === text.toLowerCase()
            );
            if (exists) return prev;

            return { tags: [...prev.tags, { id: text, text }] };
        });
    };

    handleTagDrag = (tag, currPos, newPos) => {
        this.setState((prev) => {
            const tags = [...prev.tags];
            tags.splice(currPos, 1);
            tags.splice(newPos, 0, tag);
            return { tags };
        });
    };
    getTagSuggestions = () => {
        const uniq = new Map();

        const addFrom = (arr) => {
            (arr || []).forEach((p) => {
            (p?.tags || []).forEach((t) => {
                const text = String(t || "").trim();
                if (!text) return;
                const key = text.toLowerCase();
                if (!uniq.has(key)) uniq.set(key, { id: text, text });
            });
            });
        };

        addFrom(this.state.collection);
        addFrom(this.state.library);

        return Array.from(uniq.values()).sort((a, b) => a.text.localeCompare(b.text));
    };

    confirmDeleteLibrary = (id) => {
      if (!id) return;

      confirmAlert({
        title: "Confirm delete",
        message: "Are you sure you want to delete this library prompt?",
        buttons: [
          { label: "Yes, delete", onClick: () => this.deleteLibraryConfirmed(id) },
          { label: "Cancel" },
        ],
      });
    };

    deleteLibraryConfirmed = async (id) => {
      if (!id) return;

      this.setState({ deleting: true, error: "" });

      try {
        await this.request("delete", API.libraryDelete(id));

        this.setState((prev) => ({
          library: prev.library.filter((p) => p._id !== id),
          selectedId: prev.selectedId === id ? "" : prev.selectedId,
        }));

        // if we deleted the selected item, clear the right panel too
        if (this.state.selectedId === id) {
          this.setState({
            form: { title: "", description: "", promptText: "", category: "" },
            tags: [],
            originalForm: null,
            originalTags: [],
            isEditing: false,
          });
        }
      } catch (e) {
        this.setState({ error: e.message || "Delete failed." });
      } finally {
        this.setState({ deleting: false });
      }
    };



  renderTabs() {
    const { activeTab } = this.state;
    const tabBase = "px-4 py-2 rounded-lg text-sm font-semibold border transition";
    const active = "bg-sky-900 text-white border-sky-900";
    const inactive = "bg-white text-sky-900 border-sky-200 hover:border-sky-400";

    return (
      <div className="flex gap-2 mt-3">
        <button
          className={`${tabBase} ${activeTab === "collection" ? active : inactive}`}
          onClick={() => this.setTab("collection")}
        >
          Collection
        </button>
        <button
          className={`${tabBase} ${activeTab === "library" ? active : inactive}`}
          onClick={() => this.setTab("library")}
        >
          Library
        </button>
      </div>
    );
  }

  renderListItem(item) {
    const selected = this.state.selectedId === item._id;
    const isLibrary = this.state.activeTab === "library";
    const isOwnLibrary = isLibrary && this.isOwnLibraryPrompt(item);

    return (
      <div
        key={item._id}
        onClick={() => this.selectItem(item)}
        className={[
          "cursor-pointer rounded-xl border p-3 mb-2 transition",
          selected
            ? "border-sky-900 bg-sky-50"
            : "border-sky-100 bg-white hover:border-sky-300",
        ].join(" ")}
      >
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {fmtDate(item.createdAt || item.updatedAt)}
          </div>

        {this.state.activeTab === "library" ? (
          <div className="flex items-center gap-2">
            <div className="text-xs text-slate-500">
              ❤️ {item.likes || 0} · 📄 {item.copies || 0}
            </div>

            {isOwnLibrary && (
              <button
                className="px-2 py-1 rounded-md text-xs font-semibold border border-red-200 text-red-600 hover:border-red-400"
                onClick={(e) => {
                  e.stopPropagation(); 
                  this.confirmDeleteLibrary(item._id);
                }}
                title="Delete this library prompt"
              >
                Delete
              </button>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500">
            {item.isCopied ? "Copied" : "Manual"}
          </div>
        )}

        </div>

        <div className="mt-1 text-sky-900 font-bold">
          {item.title || "Untitled"}
        </div>

        <div className="text-sm text-slate-700 line-clamp-2">
          {item.description || "—"}
        </div>
      </div>
    );
  }

 renderRightPanel() {
  const {
    activeTab,
    selectedId,
    form,
    saving,
    deleting,
    publishing,
    copying,
    liking,
    liked,
    isEditing,
  } = this.state;

  const btnBase =
    "px-3 py-2 rounded-lg text-sm font-semibold border transition inline-flex items-center gap-2";
  const btnPrimary = "bg-sky-900 text-white border-sky-900 hover:opacity-95";
  const btnGhost = "bg-white text-sky-900 border-sky-200 hover:border-sky-400";
  const btnDanger = "bg-white text-red-600 border-red-200 hover:border-red-400";
  const disabled = "opacity-50 cursor-not-allowed";

  const LikeIcon = liked ? HandThumbUpSolid : HandThumbUpOutline;

  const selectedCollection = activeTab === "collection" ? this.getSelected() : null;
  const isPublished = Boolean(selectedCollection?.isPublic);

  const selectedLibrary = activeTab === "library" ? this.getSelected() : null;
  const isOwn = activeTab === "library" ? this.isOwnLibraryPrompt(selectedLibrary) : false;

  const tagSuggestions = this.getTagSuggestions();

  const viewingExisting =
    Boolean(selectedId) && activeTab === "collection" && !isEditing;

  // ✅ collection blank state: show placeholders only (locked)
  const blankCollectionView =
    activeTab === "collection" && !selectedId && !isEditing;

  const inputsDisabled = activeTab === "library" || viewingExisting || blankCollectionView;


  return (
    <div className="rounded-2xl border border-sky-100 bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-sky-900 font-bold">
          {activeTab === "library"
            ? selectedLibrary
              ? `${fmtDate(selectedLibrary.createdAt || selectedLibrary.updatedAt)} · ${
                  (selectedLibrary.author ||"").trim () || "Unknown"                
                }`
              : "Select a prompt"
            : selectedId
            ? "Edit Prompt"
            : "New Prompt"}
        </div>


        <div className="flex gap-2">
          {activeTab === "collection" &&
            (isPublished ? (
              <button className={`${btnBase} ${btnGhost} ${disabled}`} disabled>
                Published
              </button>
            ) : (
              <button
                className={`${btnBase} ${btnGhost} ${
                  !selectedId || publishing ? disabled : ""
                }`}
                disabled={!selectedId || publishing}
                onClick={this.publishSelected}
                title="Publish this prompt to the community library"
              >
                {publishing ? "Publishing..." : "Publish"}
              </button>
            ))}

          {activeTab === "library" && (
            <>
              <button
                className={`${btnBase} ${btnGhost} ${
                  !selectedId || liking || isOwn ? disabled : ""
                }`}
                disabled={!selectedId || liking || isOwn}
                onClick={this.toggleLike}
                title={isOwn ? "You can't like your own prompt" : "Like this prompt"}
              >
                <LikeIcon
                  className={
                    liked ? "h-5 w-5 text-yellow-500" : "h-5 w-5 text-slate-500"
                  }
                />
                <span>{liked ? "Liked" : "Like"}</span>
              </button>

              <button
                className={`${btnBase} ${btnGhost} ${
                  !selectedId || copying || isOwn ? disabled : ""
                }`}
                disabled={!selectedId || copying || isOwn}
                onClick={this.copySelectedToCollection}
                title={isOwn ? "You can't copy your own prompt" : "Copy to your collection"}
              >
                <DocumentDuplicateIcon className="h-5 w-5 text-slate-500" />
                <span>{copying ? "Copying..." : "Copy"}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Title</div>
          <input
            className="w-full rounded-xl border border-sky-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
            value={form.title}
            onChange={(e) => this.handleChange("title", e.target.value)}
            placeholder="e.g. MHCP generator from transcript"
            disabled={inputsDisabled}
          />
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Description</div>
          <textarea
            className="w-full rounded-xl border border-sky-100 px-3 py-2 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
            value={form.description}
            onChange={(e) => this.handleChange("description", e.target.value)}
            placeholder="Short description..."
            disabled={inputsDisabled}
          />
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-600 mb-1">Prompt Text</div>
          <textarea
            className="w-full rounded-xl border border-sky-100 px-3 py-2 min-h-[220px] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
            value={form.promptText}
            onChange={(e) => this.handleChange("promptText", e.target.value)}
            placeholder="Paste your prompt here..."
            disabled={inputsDisabled}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Category</div>
            <select
              className="w-full rounded-xl border border-sky-100 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50"
              value={form.category || ""}
              onChange={(e) => this.handleChange("category", e.target.value)}
              disabled={inputsDisabled}
            >
              <option value="">Select category…</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-600 mb-1">Tags</div>
            <ReactTags
              tags={this.state.tags}
              suggestions={tagSuggestions}
              delimiters={[188, 13]}
              handleDelete={this.handleTagDelete}
              handleAddition={this.handleTagAdd}
              handleDrag={this.handleTagDrag}
              inputFieldPosition="top"
              placeholder="Type a tag and press Enter"
              readOnly={inputsDisabled}
              autocomplete
              classNames={{
                tags: "flex flex-wrap gap-2",
                tagInput: "w-full",
                tagInputField:
                  "w-full px-3 py-2 border border-sky-100 rounded-xl focus:ring-2 focus:ring-sky-200 disabled:bg-slate-50",
                selected: "flex flex-wrap gap-2 mt-2",
                tag:
                  "bg-sky-700 text-white px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-2",
                remove: "cursor-pointer opacity-70 hover:opacity-100",
                suggestions:
                  "bg-white border border-sky-100 rounded-xl shadow-lg mt-2",
                activeSuggestion: "bg-sky-100",
              }}
            />
          </div>
        </div>

        {activeTab === "collection" ? (
          <div className="flex items-center gap-2 pt-2">
            {!selectedId ? (
              <>
                <button
                  className={`${btnBase} ${btnPrimary} ${saving ? disabled : ""}`}
                  disabled={saving}
                  onClick={this.save}
                >
                  {saving ? "Saving..." : "Save"}
                </button>

                <button className={`${btnBase} ${btnGhost}`} onClick={this.newItem}>
                  New
                </button>
              </>
            ) : !isEditing ? (
              <>
                <button className={`${btnBase} ${btnPrimary}`} onClick={this.startEdit}>
                  Update
                </button>

                <button
                  className={`${btnBase} ${btnDanger} ${deleting ? disabled : ""}`}
                  disabled={deleting}
                  onClick={this.confirmDelete}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </>
            ) : (
              <>
                <button
                  className={`${btnBase} ${btnPrimary} ${saving ? disabled : ""}`}
                  disabled={saving}
                  onClick={this.save}
                >
                  {saving ? "Saving..." : "Save"}
                </button>

                <button className={`${btnBase} ${btnDanger}`} onClick={this.cancelEdit}>
                  Cancel
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="text-xs text-slate-500 pt-2">
            {isOwn
              ? "This is your prompt. You can't like/copy your own library prompt."
              : "Library prompts: Like or Copy into your collection."}
          </div>
        )}
      </div>
    </div>
  );
}


  render() {
    const { activeTab, loading, error } = this.state;
    const list = this.listForActive();

    return (
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-2xl font-extrabold text-sky-900">Prompt</div>
          {this.renderTabs()}

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-1 xs:grid-cols-1 gap-4">
            <div className="rounded-2xl border border-sky-100 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="text-sky-900 font-bold">
                  {activeTab === "collection" ? "My Collection" : "Community Library"}
                </div>
                <div className="flex gap-2">
                {activeTab === "collection" && (
                  <button
                    className="px-3 py-2 rounded-lg text-sm font-semibold border border-sky-200 text-sky-900 hover:border-sky-400"
                    onClick={this.newItem}
                  >
                    Add new
                  </button>
                )}

                <button
                  className="px-3 py-2 rounded-lg text-sm font-semibold border border-sky-200 text-sky-900 hover:border-sky-400"
                  onClick={this.loadActive}
                >
                  Refresh
                </button>
              </div>

              </div>

              {loading ? (
                <div className="mt-4 text-slate-600 text-sm">Loading...</div>
              ) : (
                <div className="mt-3">
                  {list.length === 0 ? (
                    <div className="text-slate-600 text-sm">No prompts yet.</div>
                  ) : (
                    list.map((item) => this.renderListItem(item))
                  )}
                </div>
              )}
            </div>

            <div>
              {error ? (
                <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}
              {this.renderRightPanel()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
